import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Send, MessageCircle, Clock, Check } from "lucide-react";
import api from "@/api";
import { UserIdentity } from "@/components/UserIdentity";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function ChatPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();

  const fmtTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString([], { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  };
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [text, setText] = useState("");
  const bottomRef = useRef(null);

  const loadConvs = useCallback(() => {
    api.get("/conversations").then((r) => setConvs(r.data)).catch(() => {});
  }, []);

  const loadMessages = useCallback((convId) => {
    api.get(`/conversations/${convId}/messages`).then((r) => {
      const msgs = r.data.messages;
      const lastOwn = [...msgs].reverse().find((m) => m.sender_id === user?.id);
      if (lastOwn) lastOwn.sent = true;
      setActive({ ...r.data, messages: msgs });
    }).catch(() => setActive(false));
  }, [user?.id]);

  useEffect(() => {
    loadConvs();
    const iv = setInterval(loadConvs, 15000);
    return () => clearInterval(iv);
  }, [loadConvs]);
  useEffect(() => {
    if (!id) return;
    loadMessages(id);
    const interval = setInterval(() => loadMessages(id), 4000);
    return () => clearInterval(interval);
  }, [id, loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [active?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    const content = text.trim();
    if (!content) return;
    const tempId = `temp-${Date.now()}`;
    const tempMsg = { id: tempId, sender_id: user?.id, text: content, created_at: new Date().toISOString(), pending: true };
    setActive((prev) => (prev ? { ...prev, messages: [...prev.messages, tempMsg] } : prev));
    setText("");
    try {
      const { data } = await api.post(`/conversations/${id}/messages`, { text: content });
      setActive((prev) => (prev ? { ...prev, messages: prev.messages.map((m) => (m.id === tempId ? { ...data, sent: true } : m)) } : prev));
      toast.success(t("common.messageSent"));
      loadConvs();
    } catch (err) {
      setActive((prev) => (prev ? { ...prev, messages: prev.messages.map((m) => (m.id === tempId ? { ...m, pending: false, failed: true } : m)) } : prev));
      toast.error(t("common.error"));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="chat-page">
      <h1 className="text-2xl md:text-3xl font-bold text-primary mb-6">{t("chat.title")}</h1>
      <div className="grid md:grid-cols-[320px_1fr] gap-5 bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden min-h-[60vh]">
        <aside className={`border-r border-slate-100 ${id ? "hidden md:block" : ""}`} data-testid="conversations-list">
          {convs.length === 0 && <p className="text-sm text-slate-400 p-6">{t("chat.noConvs")}</p>}
          {convs.map((c) => (
            <button
              key={c.id}
              data-testid={`conversation-${c.id}`}
              onClick={() => { setConvs((prev) => prev.map((x) => (x.id === c.id ? { ...x, unread: 0 } : x))); navigate(`/mensagens/${c.id}`); }}
              className={`w-full text-left p-4 border-b border-slate-50 hover:bg-slate-50 transition-colors duration-150 ${id === c.id ? "bg-slate-50" : ""}`}
            >
              <UserIdentity user={c.other_user} avatarSize="w-8 h-8" avatarText="text-xs" linkable={false} nameClass={`text-sm text-primary ${c.unread > 0 ? "font-bold" : "font-semibold"}`} testid={`conv-user-${c.id}`} />
              {c.trip && <p className="text-xs text-slate-400 mt-0.5">{c.trip.origin} → {c.trip.destination} · {c.trip.date}</p>}
              {c.last_message && (
                <div className="flex items-center justify-between gap-2 mt-1">
                  <p className={`text-xs truncate ${c.unread > 0 ? "text-slate-800 font-semibold" : "text-slate-500"}`}>{c.last_message.text}</p>
                  <span className="flex items-center gap-1.5 shrink-0">
                    {c.unread > 0 && <span data-testid={`conv-unread-${c.id}`} className="bg-accent text-white text-[10px] font-bold min-w-4.5 h-4.5 px-1 rounded-full flex items-center justify-center">{c.unread}</span>}
                    <span className="text-[10px] text-slate-400" data-testid={`conv-time-${c.id}`}>{fmtTime(c.last_message.created_at)}</span>
                  </span>
                </div>
              )}
            </button>
          ))}
        </aside>

        <section className="flex flex-col">
          {!id ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-3 p-10">
              <MessageCircle size={32} />
              <p className="text-sm">{t("chat.selectConv")}</p>
            </div>
          ) : active ? (
            <>
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3" data-testid="chat-header">
                <UserIdentity user={active.other_user} linkable={false} nameClass="font-semibold text-primary" testid="chat-header-user" avatarTestid="chat-header-avatar" />
                {active.other_user?.id && (
                  <button data-testid="chat-view-profile" onClick={() => navigate(`/utilizador/${active.other_user.id}`)} className="text-xs font-semibold text-accent hover:underline shrink-0">
                    {t("common.seeProfile")}
                  </button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3 max-h-[55vh]" data-testid="messages-thread">
                {active.messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
                      <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${mine ? "bg-accent text-white rounded-br-sm" : "bg-slate-100 text-slate-700 rounded-bl-sm"} ${m.pending ? "opacity-70" : ""}`} data-testid={`message-${m.id}`}>
                        {m.text}
                      </div>
                      <span className="flex items-center gap-1.5 text-[11px] text-slate-400 mt-0.5" data-testid={`message-time-${m.id}`}>
                        {fmtTime(m.created_at)}
                        {mine && m.pending && <span className="flex items-center gap-1" data-testid="message-status-sending">· <Clock size={11} />{t("chat.sending")}</span>}
                        {mine && m.failed && <span className="text-destructive" data-testid="message-status-failed">· {t("common.error")}</span>}
                        {mine && m.sent && <span className="flex items-center gap-1" data-testid="message-status-sent">· <Check size={11} className="text-emerald-500" />{t("chat.sent")}</span>}
                      </span>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              <form onSubmit={send} className="sticky bottom-0 p-4 border-t border-slate-100 flex gap-2 bg-white">
                <Input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder={t("chat.placeholder")} className="h-12 rounded-full" />
                <Button data-testid="message-send-button" type="submit" size="icon" className="h-12 w-12 rounded-full bg-accent hover:bg-accent/90 text-white shrink-0"><Send size={17} /></Button>
              </form>
            </>
          ) : active === false ? (
            <div className="flex-1 flex items-center justify-center p-10 text-sm text-slate-400" data-testid="chat-not-found">{t("common.notFoundTitle")}</div>
          ) : (
            <p className="p-6 text-sm text-slate-400">{t("common.loading")}</p>
          )}
        </section>
      </div>
      <p className="text-xs text-slate-400 mt-4">{t("chat.privacy")}</p>
    </div>
  );
}
